import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link } from '@tanstack/react-router'
import { LoaderCircle } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useForgotPasswordMutation } from '@/features/auth/api'
import { AuthLayout } from '@/features/auth/components/auth-layout'

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
})

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
})

type FormValues = z.infer<typeof schema>

function ForgotPasswordPage() {
  const mutation = useForgotPasswordMutation()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  })

  // Always show the same success message, regardless of whether the email
  // is registered — backend doesn't reveal that, and neither should we.
  const submitted = mutation.isSuccess

  function onSubmit(data: FormValues) {
    mutation.mutate(data)
  }

  return (
    <AuthLayout>
      <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Forgot your password?
          </h1>
          <p className="text-sm text-muted-foreground">
            Enter your email and we'll send you a link to set a new one.
          </p>
        </div>

        {submitted ? (
          <div className="rounded-xl border border-border bg-muted/50 p-4 text-sm text-foreground">
            <p className="font-medium">Check your inbox</p>
            <p className="mt-1 text-muted-foreground">
              If an account exists for that email, a reset link is on its way.
              The link expires in 60 minutes.
            </p>
          </div>
        ) : (
          <>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  aria-invalid={!!errors.email}
                  {...register('email')}
                />
                {errors.email && (
                  <FieldError>{errors.email.message}</FieldError>
                )}
              </Field>
            </FieldGroup>

            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && (
                <LoaderCircle className="size-4 animate-spin" />
              )}
              Send reset link
            </Button>
          </>
        )}

        <p className="text-center text-sm text-muted-foreground">
          Remembered it?{' '}
          <Link
            to="/login"
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            Back to sign in
          </Link>
        </p>
      </form>
    </AuthLayout>
  )
}
